import defaultDrawOptions, {
	defaultCategoryMarker,
	defaultMarker,
	drawSingleMarkerOption
} from 'config/leafletMapDrawOptions.config';
import {
	Control,
	CRS,
	Draw,
	featureGroup,
	Icon,
	ImageOverlay,
	LatLngBounds,
	marker as leafletMarker,
	Map,
	Marker
} from 'leaflet';
import 'leaflet-draw';
import { DeviceMarkerContent } from './deviceMarker';

const mapRenderer = ({
	mapRef,
	map,
	url,
	markerList,
	drawControlEnabled,
	drawSingleMarkerEnabled,
	editMarkerLocation,
	draggableMarkerLocation,
	setMap,
	setMarker,
	scrollWheelZoom,
	mapHeight,
	mapWidth,
	center,
	minZoom,
	zoom,
	setSelectedDevice,
	setVisible
}) => {
	// init
	if (mapRef.current !== null && map === null && !mapRef.current._leaflet_id) {
		const mapInstance = new Map(mapRef.current, {
			crs: CRS.Simple,
			scrollWheelZoom,
			center: center || [mapHeight / 2, mapWidth / 2],
			minZoom,
			zoom,
			attributionControl: false,
			zoomControl: false // keep default zoom hidden
		});
		if (center && zoom != null) {
			mapInstance.setView(center, zoom);
		}
		setMap(mapInstance);
	}

	// background image
	if (url && map) {
		if (!map._overlayInitialized) {
			map._overlayInitialized = true;
			const bounds = new LatLngBounds([0, 0], [mapHeight, mapWidth]);
			const imageLayer = new ImageOverlay(url, bounds);
			map.eachLayer((layer) => map.removeLayer(layer));
			imageLayer.addTo(map);
			map.setMaxBounds(bounds);
			map.fitBounds(bounds);
		}
	}

	// markers
	if (markerList && markerList.length > 0 && map) {
		markerList.forEach((marker) => {
			const iconSrc = marker.markerIcon || defaultCategoryMarker[marker.defaultCategoryID] || defaultMarker;
			const m = leafletMarker([marker.y, marker.x], {
				icon: new Icon({
					iconUrl: iconSrc,
					iconSize: marker.selectedDevice ? [70, 75] : [50, 55],
					iconAnchor: [26, 47]
				}),
				bubblingMouseEvents: true,
				riseOnHover: true
			});
			m.addTo(map);

			if (marker.uID && marker.name) {
				m.bindPopup(DeviceMarkerContent(marker));
				m.on('click', () => {
					setSelectedDevice(marker);
					setVisible(true);
				});
				m.on('mouseover', function () {
					this.openPopup();
				});
				m.on('mouseout', function () {
					this.closePopup();
				});
			}
		});
	}

	// optional draw controls
	if (drawControlEnabled && map) {
		const drawnItems = featureGroup();
		map.addLayer(drawnItems);
		if (!map.getContainer().querySelector('.leaflet-draw')) {
			const drawControl = new Control.Draw(defaultDrawOptions);
			map.addControl(drawControl);
		}
		map.on(Draw.Event.CREATED, function (e) {
			const layer = e.layer;
			layer.addTo(map);
		});
	} else if (drawSingleMarkerEnabled && map) {
		const markerDrawControl = new Control.Draw(drawSingleMarkerOption);
		if (!map.getContainer().querySelector('.leaflet-draw')) {
			map.addControl(markerDrawControl);
		}
		map.on(Draw.Event.CREATED, function (e) {
			map.removeControl(markerDrawControl);
			const layers = [];
			map.eachLayer((l) => layers.push(l));
			if (!layers.some((l) => l instanceof Marker)) {
				const layer = e.layer;
				const newMarker = new Marker(layer.getLatLng(), {
					draggable: true,
					icon: new Icon({
						iconUrl: defaultMarker,
						iconSize: [50, 55],
						iconAnchor: [26, 47]
					})
				});
				newMarker.addTo(map);
				setMarker(layer.getLatLng());
				newMarker.on('click', () => {
					newMarker.remove();
					setMarker({});
					map.addControl(markerDrawControl);
				});
				newMarker.on('dragend', () => {
					setMarker(newMarker.getLatLng());
				});
			}
		});
	} else if (draggableMarkerLocation && map && editMarkerLocation) {
		if (map._editInit) return;
		map._editInit = true;
		const existingDraw = map.getContainer().querySelector('.leaflet-draw');
		if (existingDraw) map.removeControl(existingDraw);

		const loc = editMarkerLocation[0];
		const editableMarker = new Marker([loc.y, loc.x], {
			draggable: true,
			icon: new Icon({
				iconUrl: defaultCategoryMarker[loc.defaultCategoryID] || defaultMarker,
				iconSize: [50, 55],
				iconAnchor: [26, 47]
			})
		}).addTo(map);
		setMarker({ ...editableMarker.getLatLng(), defaultCategoryID: loc.defaultCategoryID });
		editableMarker.on('dragend', () => {
			setMarker({ ...editableMarker.getLatLng(), defaultCategoryID: loc.defaultCategoryID });
		});
	}
};

export default mapRenderer;
